import { Controller } from "@hotwired/stimulus";

/**
 * CodeTabsController
 *
 * Handles switching between code examples (e.g., cURL, OpenAI SDK).
 * Each tab shows/hides corresponding code panels and syncs the copy button.
 *
 * Usage:
 *   <div data-controller="code-tabs">
 *     <div role="tablist">
 *       <button data-action="click->code-tabs#switch" data-code-tabs-index-param="0">cURL</button>
 *       <button data-action="click->code-tabs#switch" data-code-tabs-index-param="1">OpenAI SDK</button>
 *     </div>
 *     <button data-code-tabs-target="copyBtn" data-controller="clipboard" data-clipboard-text-value="...">Copy</button>
 *     <div data-code-tabs-target="panel">cURL code</div>
 *     <div data-code-tabs-target="panel" class="hidden">SDK code</div>
 *   </div>
 */
export default class CodeTabsController extends Controller {
  static targets = ["panel", "tab", "copyBtn"];

  declare panelTargets: HTMLElement[];
  declare tabTargets: HTMLElement[];
  declare copyBtnTarget: HTMLElement;
  declare hasCopyBtnTarget: boolean;

  // Store code snippets for each panel
  private codeSnippets: string[] = [];

  connect(): void {
    // Extract code from each panel on connect
    this.codeSnippets = this.panelTargets.map((panel) => {
      const pre = panel.querySelector("pre");
      return pre?.textContent?.trim() || "";
    });
  }

  /**
   * Switch to a tab when clicked
   */
  switch(event: Event): void {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const index = parseInt(target.dataset.codeTabsIndexParam || "0", 10);

    // Update tab states
    this.tabTargets.forEach((tab, i) => {
      if (i === index) {
        tab.classList.add("tab-active");
        tab.classList.add("api-lang-tab-active");
        tab.setAttribute("aria-selected", "true");
      } else {
        tab.classList.remove("tab-active");
        tab.classList.remove("api-lang-tab-active");
        tab.setAttribute("aria-selected", "false");
      }
    });

    // Show/hide panels
    this.panelTargets.forEach((panel, i) => {
      if (i === index) {
        panel.classList.remove("hidden");
      } else {
        panel.classList.add("hidden");
      }
    });

    // Sync copy button with current panel's code
    if (this.hasCopyBtnTarget && this.codeSnippets[index]) {
      this.copyBtnTarget.dataset.clipboardTextValue = this.codeSnippets[index];
    }
  }
}
